import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../../db/firebase";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  query,
  where,
} from "firebase/firestore";
import MinistryModal from "./MinistryModal";
import { useNavigate } from "react-router-dom";
import ConfirmDeleteModal from "../../../components/ConfirmDeleteModal";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context/AuthContext";
import { LoadingSpinner } from "../../../components/Loading";
import {
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineHandRaised,
  HiOutlineMagnifyingGlass,
  HiOutlineXMark,
  HiOutlineChevronRight,
  HiOutlineUsers,
} from "react-icons/hi2";

const glassBtn =
  "inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium border border-[#5932EA]/35 dark:border-[#5932EA]/40 bg-[#5932EA]/10 dark:bg-[#5932EA]/15 text-[#4c2bd4] dark:text-[#c4b5fd] backdrop-blur-sm hover:bg-[#5932EA]/18 dark:hover:bg-[#5932EA]/25 transition-colors";

const glassCard =
  "rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm overflow-hidden";

type Ministry = {
  id: string;
  name?: string;
  description?: string;
  organizationId?: string;
  members?: string[];
  leaders?: string[];
  [key: string]: any;
};

const Ministries: React.FC = () => {
  const { t } = useTranslation();
  const { organizationId } = useAuth();
  const navigate = useNavigate();
  const [ministries, setMinistries] = useState<Ministry[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [ministryToDelete, setMinistryToDelete] = useState<Ministry | null>(
    null,
  );

  useEffect(() => {
    const fetchMinistries = async () => {
      if (!organizationId) return;
      setLoading(true);
      try {
        const snap = await getDocs(
          query(
            collection(db, "ministries"),
            where("organizationId", "==", organizationId),
          ),
        );
        const ministriesData = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }) as Ministry)
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setMinistries(ministriesData);
      } catch (err) {
        console.error(t("errorFetchingMinistries"), err);
      } finally {
        setLoading(false);
      }
    };

    fetchMinistries();
  }, [organizationId, t]);

  const filteredMinistries = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ministries;
    return ministries.filter((m) => {
      const name = (m.name || "").toLowerCase();
      const description = (m.description || "").toLowerCase();
      return name.includes(q) || description.includes(q);
    });
  }, [ministries, search]);

  const handleCreate = () => setShowModal(true);
  const handleModalClose = () => setShowModal(false);

  const handleSave = async (ministry: any) => {
    if (!organizationId) return;
    setSaving(true);
    setError("");
    try {
      const newMinistryDocRef = await addDoc(collection(db, "ministries"), {
        ...ministry,
        organizationId,
        members: [],
        leaders: [],
      });
      setMinistries((prev) =>
        [
          ...prev,
          {
            id: newMinistryDocRef.id,
            ...ministry,
            organizationId,
            members: [],
            leaders: [],
          },
        ].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
      );
      handleModalClose();
    } catch (err) {
      console.error(t("errorSavingMinistry"), err);
      setError(t("errorSavingMinistry") || "Could not save ministry");
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteMinistry = (ministry: Ministry) => {
    setError("");
    setMinistryToDelete(ministry);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!ministryToDelete) return;
    setError("");
    try {
      await deleteDoc(doc(db, "ministries", ministryToDelete.id));
      setMinistries((prev) =>
        prev.filter((m) => m.id !== ministryToDelete.id),
      );
      setIsDeleteModalOpen(false);
      setMinistryToDelete(null);
    } catch (err: any) {
      console.error(t("errorDeletingMinistry"), err);
      setError(
        (t("errorDeletingMinistry") || "Could not delete ministry. ") +
          (err?.message || ""),
      );
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <LoadingSpinner size="lg" message={t("loading")} />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-4 sm:space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#5932EA]/10 text-[#5932EA] shrink-0">
            <HiOutlineHandRaised className="w-5 h-5 sm:w-6 sm:h-6" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-800 dark:text-gray-100 tracking-tight truncate">
              {t("ministries")}
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
              {ministries.length}{" "}
              {t("ministries")?.toLowerCase() || "ministries"}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleCreate}
          className={`${glassBtn} shrink-0 !px-3 sm:!px-4 touch-manipulation`}
        >
          <HiOutlinePlus className="w-5 h-5" />
          <span className="hidden sm:inline">{t("addMinistry")}</span>
        </button>
      </div>

      <div className="relative">
        <HiOutlineMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("searchMinistries")}
          className="w-full pl-11 pr-10 py-3 rounded-xl border border-gray-200/80 dark:border-gray-600/80 bg-white/70 dark:bg-gray-800/50 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA] text-base sm:text-sm"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 touch-manipulation"
            aria-label={t("clear")}
          >
            <HiOutlineXMark className="w-4 h-4" />
          </button>
        )}
      </div>

      {error && (
        <div className="px-4 py-3 rounded-xl border border-red-200/70 dark:border-red-800/50 bg-red-50/80 dark:bg-red-900/20 text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      {filteredMinistries.length === 0 ? (
        <div
          className={`${glassCard} flex flex-col items-center justify-center py-14 text-gray-500 dark:text-gray-400`}
        >
          <div className="w-14 h-14 mb-3 rounded-2xl bg-[#5932EA]/10 text-[#5932EA] flex items-center justify-center">
            <HiOutlineHandRaised className="w-7 h-7" />
          </div>
          <p className="text-sm font-medium">
            {search.trim() ? t("noSearchResults") : t("noMinistriesAvailable")}
          </p>
          {!search.trim() && (
            <button
              type="button"
              onClick={handleCreate}
              className={`${glassBtn} mt-4 touch-manipulation`}
            >
              <HiOutlinePlus className="w-4 h-4" />
              {t("addMinistry")}
            </button>
          )}
        </div>
      ) : (
        <section className={glassCard}>
          <ul className="divide-y divide-gray-100/90 dark:divide-gray-700/70">
            {filteredMinistries.map((ministry) => {
              const memberCount = ministry.members?.length || 0;
              const leaderCount = ministry.leaders?.length || 0;
              return (
                <li key={ministry.id}>
                  <div className="flex items-center gap-2.5 sm:gap-3 px-3.5 sm:px-4 py-3 hover:bg-[#5932EA]/5 dark:hover:bg-[#5932EA]/10 transition-colors">
                    <button
                      type="button"
                      onClick={() => navigate(`/ministries/${ministry.id}`)}
                      className="flex items-center gap-3 min-w-0 flex-1 text-left touch-manipulation"
                    >
                      <div className="w-10 h-10 rounded-xl bg-[#5932EA]/10 text-[#5932EA] flex items-center justify-center shrink-0">
                        <HiOutlineHandRaised className="w-5 h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
                          {ministry.name}
                        </h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                          {ministry.description || (
                            <>
                              <span className="inline-flex items-center gap-0.5">
                                <HiOutlineUsers className="w-3 h-3" />
                                {memberCount}
                              </span>
                              <span
                                className="text-gray-300 dark:text-gray-600 mx-1.5"
                                aria-hidden
                              >
                                ·
                              </span>
                              {leaderCount}{" "}
                              {t("leaders")?.toLowerCase() || "leaders"}
                            </>
                          )}
                        </p>
                      </div>
                    </button>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => confirmDeleteMinistry(ministry)}
                        className="p-2.5 rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-500/10 transition-colors touch-manipulation"
                        title={t("delete")}
                        aria-label={t("delete")}
                      >
                        <HiOutlineTrash className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                      <HiOutlineChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 hidden sm:block" />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {showModal && (
        <MinistryModal
          ministry={null}
          onSave={handleSave}
          onClose={handleModalClose}
          saving={saving}
        />
      )}
      {isDeleteModalOpen && ministryToDelete && (
        <ConfirmDeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setMinistryToDelete(null);
          }}
          onConfirm={handleDelete}
          userName={ministryToDelete.name || t("untitledMinistry") || "this ministry"}
        />
      )}
    </div>
  );
};

export default Ministries;
